import { useNavigate } from "react-router-dom";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import DirectionsCarFilledIcon from '@mui/icons-material/DirectionsCarFilled';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { Box, Button, Tab, Tabs } from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Line,
} from "recharts";
import VehicleListings from "../../features/manageVehicles/vehicleListings";
import React, { useState } from "react";
import SearchBar from "../../components/SearchBar";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const tabTypes = ["Car", "Bike", "Bicycle", "Caravan", "Trucks", "Heavy Equipment"];

const userData = [
  { name: "Mon", customers: 120, dealers: 40 },
  { name: "Tue", customers: 200, dealers: 60 },
  { name: "Wed", customers: 180, dealers: 55 },
  { name: "Thu", customers: 250, dealers: 70 },
  { name: "Fri", customers: 220, dealers: 65 },
  { name: "Sat", customers: 140, dealers: 45 },
  { name: "Sun", customers: 170, dealers: 50 },
];

const revenueData = [
  { name: "Mon", revenue: 1200 },
  { name: "Tue", revenue: 2400 },
  { name: "Wed", revenue: 1800 },
  { name: "Thu", revenue: 3200 },
  { name: "Fri", revenue: 2800 },
  { name: "Sat", revenue: 1500 },
  { name: "Sun", revenue: 2100 },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [valueTabs, setValueTabs] = React.useState(0);
  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  return (
    <>
      <div className="main_title">
        <h1>Dashboard</h1>
        <p>Welcome back! Here's what's happening with your car marketplace.</p>
      </div>

      {/* STATS */}
      <div className="stats_boxes gap_m mb_20">
        <div className="cards" onClick={() => navigate("/manage-users")}>
          <figure><PeopleAltIcon /></figure>
          <h2>2,450</h2>
          <p>Total Customers</p>
        </div>

        <div className="cards" onClick={() => navigate("/manage-dealers")}>
          <figure><WarehouseIcon /></figure>
          <h2>320</h2>
          <p>Total Dealers</p>
        </div>

        <div className="cards" onClick={() => navigate("/manage-vehicles")}>
          <figure><DirectionsCarFilledIcon /></figure>
          <h2>1,280</h2>
          <p>Total Listed Vehicles</p>
        </div>

        <div className="cards" onClick={() => navigate("/manage-revenue")}>
          <figure><AttachMoneyIcon /></figure>
          <h2>$128,450</h2>
          <p>Total Revenue</p>
        </div>
      </div>

      <div className="cards mb_20">
        <div className="cards_header mb_20">
          <h2>Featured Listings</h2>
        </div>

        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search Vehicles..."
            />
          </div>
          <div className="right_s">
            <Box className="custom_tabs" sx={{ width: "100%" }}>
              <Tabs
                value={valueTabs}
                onChange={handleChangeTabs}
                className="custom_tabs_links mb_0"
              >
                {tabTypes.map((label, index) => (
                  <Tab key={label} label={label} {...a11yProps(index)} />
                ))}
              </Tabs>
            </Box>
          </div>
        </div>
        {tabTypes.map((type, tabIndex) => (
          <CustomTabPanel key={type} value={valueTabs} index={tabIndex}>
            <VehicleListings type={type} searchTerm={searchTerm} featured={true} />
          </CustomTabPanel>
        ))}
      </div>

      {/* CHARTS */}
      <div className="grid_sc gap_m">
        {/* TOTAL USERS */}
        <div className="cards table_card">
          <div className="cards_header mb_20">
            <div className="left_s">
              <h2>Total Users</h2>
            </div>
            <div className="right_s">
              <p>
                <span>A day</span>
                <span>A week</span>
                <span>A month</span>
                <span>A year</span>
              </p>
              <Button className="btn btn_black icon_btn"><FileDownloadOutlinedIcon /> </Button>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={userData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="customers" fill="#38B6FF" name="Customers" />
              <Bar dataKey="dealers" fill="#FF9F40" name="Dealers" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* TOTAL REVENUE */}
        <div className="cards table_card">
          <div className="cards_header mb_20">
            <div className="left_s">
              <h2>Total Revenue</h2>
            </div>
            <div className="right_s">
              <p>
                <span>A day</span>
                <span>A week</span>
                <span>A month</span>
                <span>A year</span>
              </p>
              <Button className="btn btn_black icon_btn"><FileDownloadOutlinedIcon /> </Button>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#38B6FF"
                strokeWidth={3}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
};

export default Dashboard;